import React, { useRef, useState } from 'react';
import { useDialogs } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { UFOVehicleDetailsForm, UFOVehicleDetailsFormProvider } from '@michelin/core-profile-ui-tools';
import { FormHandles } from '@michelin/core-profile-ui-tools/build/components/Form/Form';
import { UFOVehicleDetailsFormData } from '@michelin/core-profile-ui-tools/build/components/UFOVehicleDetailsForm/types';
import { CountryCode } from '@michelin/core-profile-ui-tools/build/types';
import {
  useGetCustomAspectRatios,
  useGetCustomDiameters,
  useGetCustomLoads,
  useGetCustomSizes,
  useGetCustomSpeedRatings,
} from 'compositions/Wizard/SelectVehicle/customTireSize';
import {
  useGetMakes,
  useGetModels,
  useGetOptions,
  useGetStates,
  useGetTireSizes,
  useGetYears,
} from 'compositions/Wizard/SelectVehicle/vehiclesInformation';
import { FieldErrors } from 'react-hook-form/dist/types/errors';
import { match } from 'react-router-dom';
import { VehicleType } from 'types/UFOTypes';

export const getVehicleLabel = (vehicle: VehicleType) => {
  if (!vehicle.id) return 'Loading...';
  return `${vehicle.year || ''} ${vehicle.make_name || ''} ${vehicle.model_name || ''} ${vehicle.option_name || ''}`;
};

export interface CurrentRouterType {
  history: History | null;
  match: match | null;
  unlisten: (() => void) | null;
  onChange: (() => void) | null;
}

const defaultValues = {
  years: '',
  makes: '',
  models: '',
  options: '',
  tireSizes: '',
  states: '',
  customTireSizeSizes: '',
  customTireSizeAspectRatios: '',
  customTireSizeDiameters: '',
  customTireSizeLoads: '',
  customTireSizeSpeedRatings: '',
  tire_size_id: 'custom',
};

export const GetPartId = (props: { onGetSizeId: (s: number) => void }) => {
  const { onGetSizeId } = props;
  const { t } = useTranslation();
  const { errorDialog } = useDialogs();
  const ref = useRef<FormHandles>(null);
  const [modified, setModified] = useState<boolean>(false);
  const getYears = useGetYears();
  const getMakes = useGetMakes();
  const getModels = useGetModels();
  const getVehicleOptions = useGetOptions();
  const getTireSizes = useGetTireSizes();
  const getStates = useGetStates();
  const getCustomSizes = useGetCustomSizes();
  const getCustomAspectRatios = useGetCustomAspectRatios();
  const getCustomDiameters = useGetCustomDiameters();
  const getCustomLoads = useGetCustomLoads();
  const getCustomSpeedRatings = useGetCustomSpeedRatings();

  function showValidationErrors(
    errors: FieldErrors<UFOVehicleDetailsFormData> = {} as FieldErrors<UFOVehicleDetailsFormData>,
  ) {
    const msg: string[] = [];
    if (errors.year) msg.push(`• ${t('Year is required')}`);
    if (errors.make_id) msg.push(`• ${t('Make is required')}`);
    if (errors.model_id) msg.push(`• ${t('Model is required')}`);
    if (errors.option_id) msg.push(`• ${t('Option is required')}`);
    if (errors.tire_size_id) msg.push(`• ${t('Tire Size is required')}`);
    if (msg.length > 0) {
      msg.unshift(t('The following occurred before saving:'), ' ');
      errorDialog(msg, t('Missing required fields'), t('Ok'))?.catch((_) => {
        // TODO: better handle error
        // eslint-disable-next-line no-console
        console.error('An error occurred in the error dialog');
      });
    }
  }

  return (
    <>
      <UFOVehicleDetailsFormProvider
        countryCode={CountryCode.US}
        fetchYears={getYears}
        fetchMakes={getMakes}
        fetchModels={getModels}
        fetchOptions={getVehicleOptions}
        fetchTireSizes={getTireSizes}
        fetchStates={getStates}
        fetchCustomTireSizeSizes={getCustomSizes}
        fetchCustomTireSizeAspectRatios={getCustomAspectRatios}
        fetchCustomTireSizeDiameters={getCustomDiameters}
        fetchCustomTireSizeLoads={getCustomLoads}
        fetchCustomTireSizeSpeedRatings={getCustomSpeedRatings}
      >
        <UFOVehicleDetailsForm
          hideDetails
          mode="add"
          ref={ref}
          onDirtyStateChanged={(dirtyState: boolean) => {
            setModified(modified || dirtyState);
          }}
          getCustomTireSize={(ts) => onGetSizeId(Number(ts))}
          onValidationFailed={(errors: FieldErrors<UFOVehicleDetailsFormData>) => showValidationErrors(errors)}
          defaultValues={defaultValues}
        />
      </UFOVehicleDetailsFormProvider>
    </>
  );
};
